import { FC } from 'react';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LoadingButton,
} from '@thirdspacelearning/library';

import LearningObjectiveFormContent from './LearningObjectiveFormContent';

import { countryInitialValues } from '../../utils';

import {
    createLearningObjectives,
    deleteLearningObjective,
    editLearningObjective,
    LearningContentsValues,
    LearningObjectiveValues,
} from '@api';
import { CurriculumQueryKeys } from '@services';
import { Country, LearningObjective } from '@types';

const sx = {
    dialog: {
        '& .MuiDialog-paper': {
            p: 3,
            gap: 2,
        },
    },
    dialogTitle: {
        p: 0,
        textAlign: 'center',
        fontWeight: 'bold',
    },
    dialogContent: {
        p: 0,
    },
    dialogActions: {
        display: 'flex',
    },
};

interface Props {
    open: boolean;
    onClose: () => void;
    learningObjective?: LearningObjective;
    selectedTab: Country;
}

const LearningObjectiveForm: FC<Props> = ({
    open,
    onClose,
    learningObjective,
    selectedTab,
}) => {
    const queryClient = useQueryClient();

    const { mutate: createLearningObjective, isPending: isCreating } =
        useMutation({
            mutationFn: (data: any) => createLearningObjectives(data),
            onSuccess: () => {
                enqueueSnackbar('Learning objective created successfully!', {
                    key: 'create-lo-success',
                    testId: 'create-lo-success',
                    variant: 'curriculumSnackbar',
                    severity: 'success',
                });

                queryClient.invalidateQueries({
                    queryKey: [CurriculumQueryKeys.LOS],
                });

                onClose();
            },
            onError: (error: Error) => {
                enqueueSnackbar(error.message, {
                    title: 'There was an error creating the learning objective',
                    key: 'create-lo-error',
                    testId: 'create-lo-error',
                    variant: 'curriculumSnackbar',
                    severity: 'error',
                });
            },
        });

    const { mutate: updateLearningObjective, isPending: isUpdating } =
        useMutation({
            mutationFn: (data: { id: number; data: any }) =>
                editLearningObjective(data.id, data.data),
            onSuccess: () => {
                enqueueSnackbar('Learning objective edited successfully!', {
                    key: 'edit-lo-success',
                    testId: 'edit-lo-success',
                    variant: 'curriculumSnackbar',
                    severity: 'success',
                });

                queryClient.invalidateQueries({
                    queryKey: [CurriculumQueryKeys.LOS],
                });

                onClose();
            },
            onError: (error: Error) => {
                enqueueSnackbar(error?.message, {
                    title: 'There was an error editing the learning objective',
                    key: 'edit-lo-error',
                    testId: 'edit-lo-error',
                    variant: 'curriculumSnackbar',
                    severity: 'error',
                });
            },
        });

    const { mutate: deleteLO, isPending: isDeleting } = useMutation({
        mutationFn: (id: number) => deleteLearningObjective(id),
        onSuccess: () => {
            enqueueSnackbar('Learning objective deleted successfully!', {
                key: 'delete-lo-success',
                testId: 'delete-lo-success',
                variant: 'curriculumSnackbar',
                severity: 'success',
            });

            queryClient.invalidateQueries({
                queryKey: [CurriculumQueryKeys.LOS],
            });

            onClose();
        },
        onError: (error: Error) => {
            enqueueSnackbar(error?.message, {
                title: 'There was an error deleting the learning objective',
                key: 'delete-lo-error',
                testId: 'delete-lo-error',
                variant: 'curriculumSnackbar',
                severity: 'error',
            });
        },
    });

    const getLearningContents = (newLC: LearningContentsValues[] = []) => {
        const originalLC = learningObjective?.learning_contents || [];

        const originalIds = new Set(originalLC.map((i) => i.id));
        const newIds = new Set(newLC.map((u) => u.id));

        const result: LearningContentsValues[] = newLC.map((u, idx) => {
            if (u.id && originalIds.has(u.id)) {
                // Update
                return { id: u.id, title: u.title, step_number: idx };
            } else {
                // Delete
                return { step_number: idx, title: u.title };
            }
        });

        originalLC.forEach((i) => {
            if (!newIds.has(i.id)) {
                result.push({ id: i.id, _destroy: true });
            }
        });

        return result;
    };

    const onSubmit = async (values: LearningObjectiveValues) => {
        const formData = {
            country: values.country,
            title: values.title,
            year_id: Number(values.year_id),
            learning_contents_attributes:
                values.learning_contents &&
                getLearningContents(values.learning_contents),
            ai: values.ai,
        };

        let countryData = {};

        if (values.country === Country.GB) {
            countryData = {
                ...formData,
                objective_type: values.objective_type,
            };
        } else if (values.country === Country.US) {
            countryData = {
                ...formData,
                standard_codes: values.standard_codes,
            };
        }

        if (learningObjective) {
            updateLearningObjective({
                id: learningObjective.id,
                data: countryData,
            });
        } else {
            return createLearningObjective(countryData);
        }
    };
    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="addEditTitle"
            maxWidth="sm"
            fullWidth
            sx={sx.dialog}
        >
            <DialogTitle sx={sx.dialogTitle}>
                {`${learningObjective ? 'Edit' : 'Add'} TSL Learning objective`}
            </DialogTitle>
            <DialogContent sx={sx.dialogContent}>
                <Form
                    onSubmit={onSubmit}
                    mutators={{
                        ...arrayMutators,
                    }}
                    initialValues={{
                        ...countryInitialValues[
                            learningObjective?.country || selectedTab
                        ],
                        ...learningObjective,
                    }}
                    render={({
                        handleSubmit,
                        dirtySinceLastSubmit,
                        form: {
                            mutators: { push },
                        },
                    }) => (
                        <form
                            onSubmit={handleSubmit}
                            id="createNewLo"
                            noValidate
                        >
                            <LearningObjectiveFormContent
                                push={push}
                                learningObjective={learningObjective}
                                dirtySinceLastSubmit={
                                    dirtySinceLastSubmit ?? false
                                }
                                editLO={updateLearningObjective}
                                deleteLO={deleteLO}
                            />
                        </form>
                    )}
                />
            </DialogContent>
            <DialogActions sx={sx.dialogActions}>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={onClose}
                    fullWidth
                >
                    Close
                </Button>
                <LoadingButton
                    variant="contained"
                    color="primary"
                    type="submit"
                    form="createNewLo"
                    fullWidth
                    loading={isCreating || isUpdating || isDeleting}
                    disabled={isCreating || isUpdating || isDeleting}
                    data-testid="lo-submit-button"
                >
                    Save and submit
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default LearningObjectiveForm;
